import PageHero from "@/components/base/PageHero";
import ArrowLink from "@/components/base/ArrowLink";
import SectionLabel from "@/components/base/SectionLabel";
import CtaBand from "@/components/feature/CtaBand";
import Timeline from "@/pages/unternehmen/components/Timeline";
import Capabilities from "@/pages/unternehmen/components/Capabilities";
import { brandAssets, company } from "@/mocks/site";

const facts = [
  { label: "Gegründet", value: "1963 in Geschwand" },
  { label: "Generation", value: "Dritte Generation" },
  { label: "Geschäftsbereiche", value: "Maschinen · Stellplatz · E-Mobilität" },
  { label: "Geschäftsführung", value: company.manager },
  { label: "Handelsregister", value: company.register },
  { label: "USt-IdNr.", value: company.vat },
];

export default function Unternehmen() {
  return (
    <>
      <PageHero
        label="Unternehmen"
        title="Über 60 Jahre Präzision aus der Fränkischen Schweiz"
        lead="Ein Familienbetrieb in dritter Generation — gewachsen aus einer kleinen Werkstatt, geblieben ist der Anspruch: Qualität und Individualität, made in Germany."
      />

      <section className="bg-background-50 py-16 md:py-24">
        <div className="mx-auto w-full max-w-shell px-4 md:px-6">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-7">
              <SectionLabel className="mb-5">Historie</SectionLabel>
              <h2 className="max-w-xl font-heading text-[27px] font-extrabold leading-[1.12] tracking-tightest text-foreground-950 sm:text-[32px] md:text-[38px]">
                Von der Werkstatt zum Familienbetrieb.
              </h2>

              <div className="mt-7 max-w-2xl space-y-4 text-[14px] leading-[1.75] text-foreground-700 md:text-[15px]">
                <p>
                  Unser Betrieb wurde 1963 von Ulrich Arzt unter dem Namen „Ulrich Arzt
                  Maschinenbau“ in Geschwand gegründet. Neben der Herstellung kleiner
                  Sondermaschinen und Aufträgen zur Metallbearbeitung stand in der Anfangszeit vor
                  allem die Produktion von individuell gestalteten Geländern für Treppenhäuser oder
                  Balkone im Mittelpunkt — je nach Kundenwunsch entworfen und angebracht.
                </p>
                <p>
                  In den Folgejahren wurde der Maschinenpark stetig erweitert, der Fokus verlagerte
                  sich auf die Herstellung einzelner Komponenten bis hin zu kompletten Maschinen.
                  Zu dem ursprünglichen Ein-Mann-Betrieb kamen im Laufe der Jahre mehrere
                  Arbeitskräfte hinzu: 1976 stieg Karola Arzt, Tochter des Firmengründers, aktiv in
                  das Firmengeschehen ein, 1983 ihr späterer Ehemann Klaus Arzt.
                </p>
                <p>
                  Da es zunehmend an Platz mangelte, entschloss man sich zum Bau einer neuen
                  Firmenhalle. Nach deren Fertigstellung 1987 folgten der Umzug sowie neue
                  CNC-Fräs- und CNC-Drehmaschinen. Um den Firmencharakter auch nach außen deutlicher
                  zu repräsentieren, wurde das Unternehmen in „Ulrich Arzt Präzisions-Mechanik
                  Sondermaschinen“ umbenannt. Ab 1987 übernahm man zudem einen Teil der
                  Relaisproduktion der damaligen Firma Eberle — bis zu 45 weitere Arbeitskräfte
                  konnten so in der Fertigung beschäftigt werden.
                </p>
                <p>
                  Mit der Entwicklung und Herstellung unserer Produktserien für die Ausstattung von
                  Stell- und Campingplätzen entstand ein neuer Unternehmenszweig. Seit 2000 ist
                  Junior Jochen Arzt an der Zukunftsorientierung sowie der Gestaltung effektiver
                  Produktionsabläufe beteiligt. Heute besteht der Betrieb in der dritten Generation
                  und blickt auf über 60 Jahre Erfahrung zurück.
                </p>
              </div>

              <blockquote className="mt-9 max-w-2xl border-l-2 border-primary-500 pl-5">
                <p className="font-heading text-[17px] font-bold leading-snug text-foreground-950 md:text-[19px]">
                  „Wir werden auch weiterhin spezielle Lösungen und Sonderanfertigungen für unsere
                  Kunden entwickeln und umsetzen.“
                </p>
                <footer className="mt-3 font-label text-[10.5px] uppercase tracking-[0.18em] text-foreground-500">
                  {company.motto}
                </footer>
              </blockquote>
            </div>

            <aside className="lg:col-span-5">
              <div className="relative h-[220px] w-full overflow-hidden border border-foreground-200 sm:h-[280px]">
                <img
                  src={brandAssets.machineDetailAlt}
                  alt="Blick in die Fertigung der Ulrich Arzt Präzisions-Mechanik"
                  width={720}
                  height={560}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="relative overflow-hidden border border-foreground-800 bg-foreground-950 p-6 text-background-50 md:p-7">
                <div className="blueprint absolute inset-0 opacity-25" />
                <div className="relative">
                  <p className="font-label text-[10px] uppercase tracking-[0.22em] text-primary-400">
                    Auf einen Blick
                  </p>
                  <dl className="mt-5 flex flex-col divide-y divide-foreground-800">
                    {facts.map((fact) => (
                      <div
                        key={fact.label}
                        className="flex flex-col gap-1 py-3 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4"
                      >
                        <dt className="font-label text-[10px] uppercase tracking-[0.16em] text-background-500">
                          {fact.label}
                        </dt>
                        <dd className="text-left text-[13px] font-medium text-background-200 sm:text-right">
                          {fact.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <Timeline />
      <Capabilities />

      <section className="border-y border-foreground-200 bg-background-100 py-14 md:py-20">
        <div className="mx-auto grid w-full max-w-shell grid-cols-1 gap-9 px-4 md:px-6 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-6">
            <SectionLabel className="mb-5">Standort</SectionLabel>
            <h2 className="font-heading text-[26px] font-extrabold leading-[1.1] tracking-tightest text-foreground-950 sm:text-[32px] md:text-[38px]">
              Geschwand 111, Obertrubach.
            </h2>
            <p className="mt-5 max-w-lg text-[14px] leading-relaxed text-foreground-600 md:text-[15px]">
              Unser Werk liegt in Geschwand, einem Ortsteil von Obertrubach in der Fränkischen
              Schweiz — zwischen Nürnberg, Bamberg und Bayreuth. Kurze Wege zu Kunden in der Region,
              und genug Platz für Fertigung, Montage und Lager.
            </p>
            <ArrowLink to="/kontakt" className="mt-7">
              Anfahrt &amp; Kontakt
            </ArrowLink>
          </div>
          <div className="lg:col-span-6">
            <div className="border border-foreground-200 bg-background-50 p-6 md:p-7">
              <dl className="flex flex-col divide-y divide-foreground-200">
                <div className="flex items-start gap-3 pb-4">
                  <i className="ri-map-pin-2-line mt-0.5 text-[17px] text-primary-600" />
                  <div>
                    <dt className="font-label text-[10px] uppercase tracking-[0.18em] text-foreground-500">
                      Anschrift
                    </dt>
                    <dd className="mt-1 text-[14px] leading-relaxed text-foreground-900">
                      {company.street}
                      <br />
                      D-{company.zip} {company.city}
                    </dd>
                  </div>
                </div>
                <div className="flex items-start gap-3 py-4">
                  <i className="ri-phone-line mt-0.5 text-[17px] text-primary-600" />
                  <div>
                    <dt className="font-label text-[10px] uppercase tracking-[0.18em] text-foreground-500">
                      Telefon / Telefax
                    </dt>
                    <dd className="mt-1 flex flex-col text-[14px] leading-relaxed text-foreground-900">
                      <a href={`tel:${company.phoneHref}`} className="hover:text-primary-700">
                        {company.phone}
                      </a>
                      <span className="text-foreground-500">{company.fax}</span>
                    </dd>
                  </div>
                </div>
                <div className="flex items-start gap-3 pt-4">
                  <i className="ri-time-line mt-0.5 text-[17px] text-primary-600" />
                  <div>
                    <dt className="font-label text-[10px] uppercase tracking-[0.18em] text-foreground-500">
                      Geschäftszeiten
                    </dt>
                    <dd className="mt-1 text-[13.5px] leading-relaxed text-foreground-700">
                      {company.hours.map((entry) => (
                        <span key={entry.days} className="block">
                          {entry.days}: {entry.time}
                        </span>
                      ))}
                    </dd>
                  </div>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      <CtaBand
        title="Lernen Sie uns persönlich kennen."
        text="Ein Besuch in Geschwand sagt oft mehr als jede Broschüre. Melden Sie sich vorab — wir nehmen uns Zeit."
      />
    </>
  );
}